import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/core/styles";
import { CircularProgress } from "@material-ui/core";
import CustomButton from "../shared/custom-button";
import {
  DEFAULT_SUBJECT,
  IFRAME_NAME,
  MIN_FILL_TIME_MS,
  SUBMIT_TIMEOUT_MS,
  ZOHO_ACTION_TYPE,
  ZOHO_ACTION_URL,
  ZOHO_FIELD_NAMES,
  ZOHO_TOKENS,
} from "../../constants/zoho-form";

const EMAIL_REGEX = /^([\w_][\w\-_.+'&]*)@(?=.{4,256}$)(([\w]+)([\-_]*[\w])*[\.])+[a-zA-Z]{2,22}$/;

const formatUSPhone = (raw) => {
  const d = (raw || "").replace(/\D/g, "").slice(0, 10);
  if (d.length === 0) return "";
  if (d.length <= 3) return `(${d}`;
  if (d.length <= 6) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
};

const useStyles = makeStyles((theme) => ({
  form: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    columnGap: 18,
    rowGap: 18,
    width: "100%",
    [theme.breakpoints.down(840)]: {
      gridTemplateColumns: "1fr",
    },
  },
  fieldFull: {
    gridColumn: "1 / -1",
  },
  fieldGroup: {
    display: "flex",
    flexDirection: "column",
    minWidth: 0,
  },
  label: {
    color: "#0b365d",
    fontSize: "0.875rem",
    fontWeight: 500,
    marginBottom: 6,
    fontFamily: theme.typography.fontFamily,
  },
  required: {
    color: theme.colors.orange,
    marginLeft: 3,
  },
  input: {
    background: theme.palette.background.lightGrey,
    border: "1px solid #d4d4d4",
    borderRadius: theme.shape.borderRadius,
    fontFamily: theme.typography.fontFamily,
    fontSize: "1rem",
    color: "#000",
    padding: "12px 16px",
    width: "100%",
    boxSizing: "border-box",
    outline: "none",
    transition: "border-color 120ms ease",
    "&:focus": { borderColor: theme.colors.orange },
    "&[aria-invalid='true']": { borderColor: theme.colors.orangeGradientDark },
  },
  textarea: {
    minHeight: 120,
    resize: "vertical",
    fontFamily: theme.typography.fontFamily,
  },
  helperError: {
    color: theme.colors.orangeGradientDark,
    fontSize: "0.8rem",
    marginTop: 4,
  },
  honeypot: {
    position: "absolute",
    left: -10000,
    top: "auto",
    width: 1,
    height: 1,
    overflow: "hidden",
  },
  submitRow: {
    gridColumn: "1 / -1",
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
    marginTop: 8,
    [theme.breakpoints.down(840)]: {
      "& > button": { width: "100%" },
    },
  },
  submitButton: {
    display: "inline-flex",
    alignItems: "center",
    gap: 10,
  },
  spinner: {
    color: theme.colors.orange,
    marginLeft: 8,
  },
  hiddenIframe: {
    display: "none",
  },
  successPanel: {
    background: theme.palette.background.lightGrey,
    border: "1px solid #d4d4d4",
    borderRadius: theme.shape.borderRadius,
    padding: "32px 28px",
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: 16,
  },
  successHeading: {
    color: "#0b365d",
    fontSize: "1.4rem",
    fontWeight: 700,
    margin: 0,
  },
  successText: {
    color: theme.palette.text.secondary,
    fontSize: "1rem",
    margin: 0,
  },
}));

const ZohoContactForm = ({ defaultSubject, defaultMessage }) => {
  const classes = useStyles();
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [organization, setOrganization] = useState("");
  const [description, setDescription] = useState(defaultMessage || "");
  const [honeypot, setHoneypot] = useState("");

  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [actionUrl, setActionUrl] = useState("");

  const mountTimeRef = useRef(0);
  const awaitingRef = useRef(false);
  const timeoutRef = useRef(null);
  const successHeadingRef = useRef(null);

  const trimmedSubject = (defaultSubject || "").trim();
  const subjectValue = trimmedSubject || DEFAULT_SUBJECT;

  useEffect(() => {
    mountTimeRef.current = Date.now();
    setActionUrl(ZOHO_ACTION_URL);
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  useEffect(() => {
    if (showSuccess && successHeadingRef.current) {
      successHeadingRef.current.focus();
    }
  }, [showSuccess]);

  const validate = () => {
    const next = {};
    if (!lastName.trim()) next.lastName = "Please provide your last name";
    if (!email.trim()) {
      next.email = "Please provide your email";
    } else if (!EMAIL_REGEX.test(email.trim())) {
      next.email = "Please enter a valid email address";
    }
    if (!organization.trim()) {
      next.organization = "Please provide your organization's name";
    }
    if (!description.trim()) {
      next.description = "Please provide a message";
    }
    if (phone.trim()) {
      const digits = phone.replace(/\D/g, "");
      if (digits.length !== 10) {
        next.phone = "Please enter a 10-digit US phone number";
      }
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const onPhoneChange = (e) => setPhone(formatUSPhone(e.target.value));

  const renderField = ({
    id,
    name,
    label,
    type = "text",
    value,
    onChange,
    required = false,
    maxLength = 120,
    autoComplete,
    inputMode,
    fullWidth = false,
  }) => {
    const hasError = Boolean(errors[id]);
    const errorId = hasError ? `${id}-error` : undefined;
    return (
      <div className={`${classes.fieldGroup} ${fullWidth ? classes.fieldFull : ""}`}>
        <label htmlFor={id} className={classes.label}>
          {label}
          {required && <span className={classes.required} aria-hidden="true">*</span>}
        </label>
        <input
          id={id}
          name={name}
          type={type}
          value={value}
          onChange={onChange}
          maxLength={maxLength}
          autoComplete={autoComplete}
          inputMode={inputMode}
          aria-required={required ? "true" : undefined}
          aria-invalid={hasError ? "true" : undefined}
          aria-describedby={errorId}
          className={classes.input}
        />
        {hasError && (
          <span id={errorId} role="alert" className={classes.helperError}>
            {errors[id]}
          </span>
        )}
      </div>
    );
  };

  const triggerSuccessSilently = (e) => {
    e.preventDefault();
    setShowSuccess(true);
  };

  const handleSubmit = (e) => {
    if (submitting) {
      e.preventDefault();
      return;
    }
    if (!validate()) {
      e.preventDefault();
      return;
    }
    if (honeypot) {
      triggerSuccessSilently(e);
      return;
    }
    const elapsed = Date.now() - mountTimeRef.current;
    if (elapsed < MIN_FILL_TIME_MS) {
      triggerSuccessSilently(e);
      return;
    }
    if (!actionUrl) {
      e.preventDefault();
      return;
    }
    setSubmitting(true);
    awaitingRef.current = true;
    timeoutRef.current = setTimeout(() => {
      if (awaitingRef.current) {
        awaitingRef.current = false;
        setSubmitting(false);
        setShowSuccess(true);
      }
    }, SUBMIT_TIMEOUT_MS);
  };

  const handleIframeLoad = () => {
    // Ignore the initial about:blank load; only react to load events triggered by our own submit.
    if (!awaitingRef.current) return;
    awaitingRef.current = false;
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setSubmitting(false);
    setShowSuccess(true);
  };

  if (showSuccess) {
    return (
      <div className={classes.successPanel} role="status" aria-live="polite">
        <h3 ref={successHeadingRef} tabIndex={-1} className={classes.successHeading}>Thanks &mdash; we'll be in touch shortly.</h3>
        <p className={classes.successText}>
          Your message has been received. A member of our team will get back to you as soon as possible.
        </p>
        <CustomButton color="primary" variant="contained" href="/">
          Back to home
        </CustomButton>
      </div>
    );
  }

  return (
    <>
    <form action={actionUrl} method="POST" encType="multipart/form-data" target={IFRAME_NAME} onSubmit={handleSubmit} noValidate className={classes.form}>
      <input type="hidden" name="xnQsjsdp" value={ZOHO_TOKENS.xnQsjsdp} />
      <input type="hidden" name="xmIwtLD" value={ZOHO_TOKENS.xmIwtLD} />
      <input type="hidden" name="xJdfEaS" value="" />
      <input type="hidden" name="actionType" value={ZOHO_ACTION_TYPE} />
      <input type="hidden" name={ZOHO_FIELD_NAMES.subject} value={subjectValue} />
      {/* Honeypot: hidden from AT and non-focusable; humans never see or fill this */}
      <div className={classes.honeypot} aria-hidden="true">
        <label htmlFor="website-url">
          Website
          <input
            id="website-url"
            name="website"
            type="text"
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
            value={honeypot}
            onChange={(e) => setHoneypot(e.target.value)}
          />
        </label>
      </div>

      {renderField({
        id: "firstName",
        name: ZOHO_FIELD_NAMES.firstName,
        label: "First Name",
        value: firstName,
        onChange: (e) => setFirstName(e.target.value),
        autoComplete: "given-name",
      })}

      {renderField({
        id: "lastName",
        name: ZOHO_FIELD_NAMES.lastName,
        label: "Last Name",
        value: lastName,
        onChange: (e) => setLastName(e.target.value),
        required: true,
        autoComplete: "family-name",
      })}

      {renderField({
        id: "email",
        name: ZOHO_FIELD_NAMES.email,
        label: "Email",
        type: "email",
        value: email,
        onChange: (e) => setEmail(e.target.value),
        required: true,
        autoComplete: "email",
      })}

      {renderField({
        id: "phone",
        name: ZOHO_FIELD_NAMES.phone,
        label: "Phone number",
        type: "tel",
        value: phone,
        onChange: onPhoneChange,
        autoComplete: "tel-national",
        inputMode: "numeric",
      })}

      {renderField({
        id: "organization",
        name: ZOHO_FIELD_NAMES.organization,
        label: "Organization",
        value: organization,
        onChange: (e) => setOrganization(e.target.value),
        required: true,
        autoComplete: "organization",
        fullWidth: true,
      })}

      <div className={`${classes.fieldGroup} ${classes.fieldFull}`}>
        <label htmlFor="description" className={classes.label}>
          More Details
          <span className={classes.required} aria-hidden="true">*</span>
        </label>
        <textarea
          id="description"
          name={ZOHO_FIELD_NAMES.description}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          maxLength={3000}
          aria-required="true"
          aria-invalid={errors.description ? "true" : undefined}
          aria-describedby={errors.description ? "description-error" : undefined}
          className={`${classes.input} ${classes.textarea}`}
        />
        {errors.description && (
          <span id="description-error" role="alert" className={classes.helperError}>
            {errors.description}
          </span>
        )}
      </div>

      <div className={classes.submitRow}>
        <CustomButton
          color="primary"
          variant="contained"
          type="submit"
          disabled={submitting}
          className={classes.submitButton}
        >
          {submitting ? "Sending" : "Send Message"}
          {submitting && <CircularProgress size={14} className={classes.spinner} />}
        </CustomButton>
      </div>
    </form>

    <iframe
      name={IFRAME_NAME}
      title="Zoho form submission target"
      className={classes.hiddenIframe}
      onLoad={handleIframeLoad}
    />
    </>
  );
};

ZohoContactForm.propTypes = {
  defaultSubject: PropTypes.string,
  defaultMessage: PropTypes.string,
};

export default ZohoContactForm;
